import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelSharingUserRoleType } from '@view-model/domain/view-model';
import type { Workspace } from '@workspace/domain/workspace/Workspace';
import { SettingForm } from './SettingForm';
import { usePrevious } from '@view-model/models/common/hooks/usePrevious';
import { trpcClient } from '@framework/trpcClient';

type Props = {
  isOpen: boolean;
  onClose(): void;
  workspace: Workspace;
  viewModel: ViewModelEntity;
};

type ViewModelSetting = {
  name: string;
  sharingUserRole: ViewModelSharingUserRoleType;
  isLocked: boolean;
};

export const ViewModelSettingModal: React.FC<Props> = ({ isOpen, onClose, workspace, viewModel }: Props) => {
  const { t } = useTranslation();
  const previousIsOpen = usePrevious(isOpen);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [viewModelSetting, setViewModelSetting] = useState<ViewModelSetting>({
    name: '',
    sharingUserRole: ViewModelSharingUserRoleType.None,
    isLocked: false,
  });
  const canSave = viewModelSetting.name.trim() !== '';

  useEffect(() => {
    if (isOpen && !previousIsOpen) {
      // モーダルを開いた時に、各stateにビューモデル・エンティティの情報を設定する
      setViewModelSetting({
        name: viewModel.name.value,
        sharingUserRole: viewModel.sharingUserRole,
        isLocked: viewModel.isLocked,
      });
    }
  }, [isOpen, previousIsOpen, viewModel.isLocked, viewModel.name.value, viewModel.sharingUserRole]);

  const handleChangeName = useCallback(
    (name: string) =>
      setViewModelSetting({
        ...viewModelSetting,
        name,
      }),
    [viewModelSetting]
  );

  const handleChangeSharingUserRole = useCallback(
    (role: ViewModelSharingUserRoleType) =>
      setViewModelSetting({
        ...viewModelSetting,
        sharingUserRole: role,
      }),
    [viewModelSetting]
  );

  const handleChangeIsLocked = useCallback(
    (isLocked: boolean) =>
      setViewModelSetting({
        ...viewModelSetting,
        isLocked,
      }),
    [viewModelSetting]
  );

  const handleSubmit = useCallback(async () => {
    if (viewModelSetting.name.trim() === '') return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await trpcClient.viewModel.updateViewModelSettings.mutate({
        viewModelId: viewModel.id,
        name: viewModelSetting.name,
        sharingUserRole: viewModelSetting.sharingUserRole,
        isLocked: viewModelSetting.isLocked,
      });
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMessage(t('canvas.viewModelSetting.updateError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [onClose, t, viewModel.id, viewModelSetting.isLocked, viewModelSetting.name, viewModelSetting.sharingUserRole]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      submitButton={
        <Button color="primary" onClick={handleSubmit} disabled={!canSave} loading={isSubmitting}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          {t('common.action.cancel')}
        </Button>
      }
      title={t('viewModel.viewModelSetting.label')}
    >
      <div className="p-4" style={{ width: '600px' }}>
        <SettingForm
          viewModelURLShareable={workspace.setting.isViewModelURLShareable}
          isPublicWorkspace={workspace.setting.isPublicSpace}
          name={viewModelSetting.name}
          onChangeName={handleChangeName}
          sharingUserRole={viewModelSetting.sharingUserRole}
          onChangeSharingUserRole={handleChangeSharingUserRole}
          isLocked={viewModelSetting.isLocked}
          onChangeIsLocked={handleChangeIsLocked}
          disabled={isSubmitting}
        />

        {errorMessage && <div className="py-2 text-red-500">{errorMessage}</div>}
      </div>
    </GeneralModal>
  );
};
